import React from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { FormattedMessage, useIntl } from 'react-intl';
import { Link } from 'react-router-dom';

import LandingSection from '../../../MyComponents/LandingSections/LandingSection';
import SectionEyebrow from '../../../MyComponents/LandingSections/SectionEyebrow';
import SectionHeading from '../../../MyComponents/LandingSections/SectionHeading';
import FeatureMedia from './FeatureMedia';
import SubjectTabs from './SubjectTabs';
import { START_FREE_LINK } from '../../../common/urlLinks';
import { REDESIGN_COLORS, REDESIGN_FONTS, REDESIGN_TINTS as TINTS } from '../../../constants';

const ASSETS = '/redesign/';

// The subject tabs; every one has a screenshot per language (see subjectImage).
const SUBJECTS = [
    { key: 'primary', tint: TINTS.amber, bullets: 4 },
    { key: 'romanian', tint: TINTS.linen, bullets: 3 },
    { key: 'geography', tint: TINTS.sky, bullets: 3 },
    { key: 'history', tint: TINTS.apricot, bullets: 3 },
    { key: 'maths', tint: TINTS.periwinkle, bullets: 3 },
];
// Each tab's screenshot, in the visitor's language: subject-<key>.<locale>.webp.
const subjectImage = (key, locale) => ({ src: `${ASSETS}subject-${key}.${locale}.webp`, fit: 'cover', position: 'center top' });

const PAIR_ONE_COL = '@media (max-width: 900px)';
const TWO_COL_ONE_COL = '@media (max-width: 860px)';

const useStyles = makeStyles(() => ({
    phase: {
        display: 'flex',
        alignItems: 'center',
        gap: '14px',
        margin: '0 0 20px',
    },
    phaseNumber: {
        color: REDESIGN_COLORS.accent,
        fontSize: '0.82rem',
        fontWeight: REDESIGN_FONTS.weightHeading,
    },
    phaseLabel: {
        color: REDESIGN_COLORS.muted,
        fontSize: '0.74rem',
        fontWeight: REDESIGN_FONTS.weightHeading,
        letterSpacing: '0.14em',
        textTransform: 'uppercase',
    },
    phaseRule: {
        flex: 1,
        height: '1px',
        background: REDESIGN_COLORS.ruleSand,
    },
    pair: {
        display: 'grid',
        gridTemplateColumns: 'minmax(0, 1.4fr) minmax(0, 1fr)',
        gap: '22px',
        alignItems: 'stretch',
        marginBottom: '52px',
        [PAIR_ONE_COL]: {
            gridTemplateColumns: '1fr',
        },
    },
    card: {
        boxSizing: 'border-box',
        padding: 'clamp(24px, 2.6vw, 34px)',
        border: `1px solid ${REDESIGN_COLORS.sand}`,
        borderRadius: '24px',
        background: REDESIGN_COLORS.white,
    },
    cardColumn: {
        display: 'flex',
        flexDirection: 'column',
    },
    cardBlock: {
        marginBottom: '52px',
    },
    cardLast: {
        marginBottom: '44px',
    },
    cardDark: {
        boxSizing: 'border-box',
        position: 'relative',
        // Clips the ring that bleeds off the bottom-left corner.
        overflow: 'hidden',
        alignItems: 'center',
        padding: 'clamp(28px, 3vw, 44px)',
        borderRadius: '24px',
        color: REDESIGN_COLORS.white,
        background: REDESIGN_COLORS.dark,
    },
    twoCol: {
        display: 'grid',
        gridTemplateColumns: 'minmax(0, 0.85fr) minmax(0, 1.15fr)',
        gap: 'clamp(26px, 3.6vw, 46px)',
        [TWO_COL_ONE_COL]: {
            gridTemplateColumns: '1fr',
            gap: '26px',
        },
    },
    // On one column the copy should introduce the screenshot, not follow it.
    mediaFirst: {
        gridTemplateColumns: 'minmax(0, 1.15fr) minmax(0, 0.85fr)',
        alignItems: 'center',
        [TWO_COL_ONE_COL]: {
            gridTemplateColumns: '1fr',
            '& > :first-child': {
                order: 2,
            },
        },
    },
    onDarkCopy: {
        position: 'relative',
        zIndex: 2,
        display: 'flex',
        flexDirection: 'column',
    },
    onDarkMedia: {
        position: 'relative',
        zIndex: 2,
    },
    ring: {
        position: 'absolute',
        width: '230px',
        height: '230px',
        left: '-90px',
        bottom: '-110px',
        border: '38px solid rgba(16, 169, 179, 0.28)',
        borderRadius: '50%',
        pointerEvents: 'none',
    },
    eyebrow: {
        alignSelf: 'flex-start',
        marginBottom: '13px',
        padding: '6px 11px',
        border: `1px solid ${REDESIGN_COLORS.primaryBorderSoft}`,
        background: REDESIGN_COLORS.mist,
        letterSpacing: '0.09em',
    },
    eyebrowOnDark: {
        borderColor: 'rgba(255, 255, 255, 0.16)',
        color: REDESIGN_COLORS.mist,
        background: 'rgba(255, 255, 255, 0.08)',
    },
    title: {
        margin: '0 0 11px',
        maxWidth: '26ch',
        fontSize: 'clamp(1.45rem, 2.5vw, 2.1rem)',
        lineHeight: 1.1,
        letterSpacing: '-0.03em',
        fontWeight: REDESIGN_FONTS.weightHeading,
        color: REDESIGN_COLORS.dark,
    },
    titleCompact: {
        maxWidth: 'none',
        fontSize: 'clamp(1.35rem, 2.1vw, 1.75rem)',
        lineHeight: 1.12,
    },
    titleWide: {
        maxWidth: 'none',
    },
    titleOnDark: {
        maxWidth: '24ch',
        fontSize: 'clamp(1.6rem, 2.9vw, 2.4rem)',
        lineHeight: 1.08,
        color: REDESIGN_COLORS.white,
    },
    subtitle: {
        margin: '0 0 10px',
        maxWidth: '26ch',
        fontSize: 'clamp(1.3rem, 2.2vw, 1.85rem)',
        lineHeight: 1.12,
        letterSpacing: '-0.03em',
        fontWeight: REDESIGN_FONTS.weightHeading,
        color: REDESIGN_COLORS.dark,
    },
    text: {
        margin: 0,
        color: REDESIGN_COLORS.body,
        fontSize: '1rem',
        lineHeight: 1.6,
    },
    textCompact: {
        fontSize: '0.98rem',
    },
    textOnDark: {
        color: REDESIGN_COLORS.bodyOnDark,
        fontSize: '1.02rem',
    },
    bullets: {
        display: 'grid',
        gap: '10px',
        margin: '16px 0 0',
        padding: 0,
        listStyle: 'none',
        color: REDESIGN_COLORS.body,
        fontSize: '0.9rem',
        fontWeight: REDESIGN_FONTS.weightStrong,
    },
    bulletsOnDark: {
        color: REDESIGN_COLORS.bodyOnDark,
    },
    bullet: {
        display: 'flex',
        gap: '9px',
    },
    tick: {
        color: REDESIGN_COLORS.primary,
        fontWeight: REDESIGN_FONTS.weightHeading,
    },
    mediaBelow: {
        marginTop: '24px',
    },
    // The AI card is the shorter of the pair; its screenshot sits at the bottom
    // so the two mats line up.
    mediaBelowPinned: {
        marginTop: 'auto',
        paddingTop: '24px',
    },
    subjectsIntro: {
        maxWidth: '720px',
        marginBottom: '22px',
    },
    tabs: {
        marginBottom: '22px',
    },
    closing: {
        display: 'grid',
        justifyItems: 'center',
        gap: '14px',
        maxWidth: '520px',
        margin: '0 auto',
        textAlign: 'center',
    },
    closingCopy: {
        display: 'grid',
        gap: '5px',
    },
    closingTitle: {
        margin: 0,
        fontSize: '1.1rem',
        lineHeight: 1.25,
        letterSpacing: '-0.02em',
        fontWeight: REDESIGN_FONTS.weightHeading,
        color: REDESIGN_COLORS.dark,
    },
    closingNote: {
        margin: 0,
        color: REDESIGN_COLORS.muted,
        fontSize: '0.82rem',
        fontWeight: 600,
    },
    closingCta: {
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '46px',
        padding: '0 22px',
        borderRadius: '999px',
        color: REDESIGN_COLORS.white,
        background: REDESIGN_COLORS.primary,
        fontSize: '0.94rem',
        fontWeight: REDESIGN_FONTS.weightHeading,
        '&:hover': {
            color: REDESIGN_COLORS.white,
            background: REDESIGN_COLORS.primaryHover,
        },
    },
}));

const cx = (...names) => names.filter(Boolean).join(' ');

/**
 * The `#instrumente` band: what a teacher does in a lesson, in four numbered
 * phases - prepare (materials + AI), teach (one card with a tab per subject),
 * engage (individual boards) and keep (library + export) - then one CTA.
 */
const HomeTools = () => {
    const classes = useStyles();
    const intl = useIntl();
    const [subject, setSubject] = React.useState(SUBJECTS[0].key);

    const t = (id) => intl.formatMessage({ id: `redesign.home.tools.${id}` });
    const active = SUBJECTS.find((s) => s.key === subject);
    const image = subjectImage(active.key, intl.locale);

    const eyebrow = (prefix, onDark) => (
        <SectionEyebrow variant="card" className={cx(classes.eyebrow, onDark && classes.eyebrowOnDark)}>
            <FormattedMessage id={`redesign.home.tools.${prefix}.label`} />
        </SectionEyebrow>
    );

    const bullets = (prefix, count, onDark) => (
        <ul className={cx(classes.bullets, onDark && classes.bulletsOnDark)}>
            {Array.from({ length: count }, (_, i) => (
                <li key={i} className={classes.bullet}>
                    <span aria-hidden="true" className={classes.tick}>
                        ✓
                    </span>
                    <FormattedMessage id={`redesign.home.tools.${prefix}.bullet${i + 1}`} />
                </li>
            ))}
        </ul>
    );

    const phase = (number, key) => (
        <div className={classes.phase}>
            <span className={classes.phaseNumber}>{number}</span>
            <span className={classes.phaseLabel}>
                <FormattedMessage id={`redesign.home.tools.phase.${key}`} />
            </span>
            <span aria-hidden="true" className={classes.phaseRule} />
        </div>
    );

    return (
        <LandingSection id="instrumente" paddingTop={64} paddingBottom={72}>
            <SectionHeading
                eyebrow={<FormattedMessage id="redesign.home.tools.eyebrow" />}
                title={<FormattedMessage id="redesign.home.tools.title" />}
                lead={<FormattedMessage id="redesign.home.tools.lead" />}
                marginBottom={44}
            />

            {phase('01', 'prepare')}
            <div className={classes.pair}>
                <article className={cx(classes.card, classes.cardColumn)}>
                    {eyebrow('material')}
                    <h3 className={classes.title}>
                        <FormattedMessage id="redesign.home.tools.material.title" />
                    </h3>
                    <p className={classes.text}>
                        <FormattedMessage id="redesign.home.tools.material.text" />
                    </p>
                    {bullets('material', 3)}
                    <div className={classes.mediaBelow}>
                        <FeatureMedia
                            tint={TINTS.mint}
                            src={`${ASSETS}feature-material-exercitiu.${intl.locale}.webp`}
                            alt={t('material.alt')}
                        />
                    </div>
                </article>

                <article className={cx(classes.card, classes.cardColumn)}>
                    {eyebrow('ai')}
                    <h3 className={cx(classes.title, classes.titleCompact)}>
                        <FormattedMessage id="redesign.home.tools.ai.title" />
                    </h3>
                    <p className={cx(classes.text, classes.textCompact)}>
                        <FormattedMessage id="redesign.home.tools.ai.text" />
                    </p>
                    {bullets('ai', 3)}
                    <div className={classes.mediaBelowPinned}>
                        <FeatureMedia
                            tint={TINTS.peach}
                            src={`${ASSETS}feature-asistent-ai.${intl.locale}.webp`}
                            alt={t('ai.alt')}
                            fit="cover"
                            position="center top"
                        />
                    </div>
                </article>
            </div>

            {phase('02', 'teach')}
            <article className={cx(classes.card, classes.cardBlock)}>
                <div className={classes.subjectsIntro}>
                    {eyebrow('subjects')}
                    <h3 className={cx(classes.title, classes.titleWide)}>
                        <FormattedMessage id="redesign.home.tools.subjects.title" />
                    </h3>
                    <p className={classes.text}>
                        <FormattedMessage id="redesign.home.tools.subjects.text" />
                    </p>
                </div>

                <SubjectTabs
                    idPrefix="subject"
                    ariaLabel={t('subjects.tabs.aria')}
                    items={SUBJECTS.map((s) => ({ key: s.key, label: t(`subjects.${s.key}.tab`) }))}
                    value={subject}
                    onChange={setSubject}
                    className={classes.tabs}
                />

                <div
                    role="tabpanel"
                    id={`subject-panel-${active.key}`}
                    aria-labelledby={`subject-tab-${active.key}`}
                    className={cx(classes.twoCol, classes.mediaFirst)}
                >
                    <FeatureMedia
                        tint={active.tint}
                        src={image.src}
                        fit={image.fit}
                        position={image.position}
                        alt={t(`subjects.${active.key}.alt`)}
                    />
                    <div>
                        <h4 className={classes.subtitle}>
                            <FormattedMessage id={`redesign.home.tools.subjects.${active.key}.title`} />
                        </h4>
                        <p className={classes.text}>
                            <FormattedMessage id={`redesign.home.tools.subjects.${active.key}.text`} />
                        </p>
                        {bullets(`subjects.${active.key}`, active.bullets)}
                    </div>
                </div>
            </article>

            {phase('03', 'engage')}
            <article className={cx(classes.twoCol, classes.cardDark, classes.cardBlock)}>
                <div className={classes.onDarkCopy}>
                    {eyebrow('boards', true)}
                    <h3 className={cx(classes.title, classes.titleOnDark)}>
                        <FormattedMessage id="redesign.home.tools.boards.title" />
                    </h3>
                    <p className={cx(classes.text, classes.textOnDark)}>
                        <FormattedMessage id="redesign.home.tools.boards.text" />
                    </p>
                    {bullets('boards', 4, true)}
                </div>
                <div className={classes.onDarkMedia}>
                    <FeatureMedia
                        onDark
                        src={`${ASSETS}feature-table-individuale.${intl.locale}.webp`}
                        alt={t('boards.alt')}
                        fit="cover"
                    />
                </div>
                <div aria-hidden="true" className={classes.ring} />
            </article>

            {phase('04', 'keep')}
            <article className={cx(classes.card, classes.twoCol, classes.mediaFirst, classes.cardLast)}>
                <FeatureMedia
                    tint={TINTS.amber}
                    src={`${ASSETS}feature-biblioteca.${intl.locale}.webp`}
                    alt={t('library.alt')}
                    fit="cover"
                />
                <div>
                    {eyebrow('library')}
                    <h3 className={classes.title}>
                        <FormattedMessage id="redesign.home.tools.library.title" />
                    </h3>
                    <p className={classes.text}>
                        <FormattedMessage id="redesign.home.tools.library.text" />
                    </p>
                    {bullets('library', 4)}
                </div>
            </article>

            <div className={classes.closing}>
                <div className={classes.closingCopy}>
                    <h3 className={classes.closingTitle}>
                        <FormattedMessage id="redesign.home.tools.cta.title" />
                    </h3>
                    <p className={classes.closingNote}>
                        <FormattedMessage id="redesign.home.tools.cta.note" />
                    </p>
                </div>
                <Link to={START_FREE_LINK} className={classes.closingCta}>
                    <FormattedMessage id="redesign.home.tools.cta.button" />
                </Link>
            </div>
        </LandingSection>
    );
};

export default HomeTools;
